import { useEffect, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ShieldAlert, ShieldCheck } from 'lucide-react';
import { useRoleCheck } from '@/hooks/use-role-check';

interface SecurityHeader {
  name: string;
  present: boolean;
  value?: string;
  severity: 'critical' | 'high' | 'medium';
  description: string;
}

/**
 * Security Headers Check Component
 * Verifies critical security headers are present
 * Only visible to super_admin and admin users
 * 
 * Note: This component is disabled in development and preview environments
 * as security headers are only applied in production deployments
 */
export function SecurityHeadersCheck() {
  const { data: roleData } = useRoleCheck();
  const [headers, setHeaders] = useState<SecurityHeader[]>([]);
  const [showAlert, setShowAlert] = useState(false);

  // Only show to super_admin and admin
  const canView = roleData?.isSuperAdmin || roleData?.isAdmin;

  // Always hide in development - headers only apply in production
  const isDevelopment = import.meta.env.DEV;
  
  useEffect(() => {
    if (isDevelopment || !canView) return;

    const checkHeaders = async () => {
      try {
        // Make a request to check response headers
        const response = await fetch(window.location.href, { method: 'HEAD' });
        
        const checkedHeaders: SecurityHeader[] = [
          {
            name: 'Content-Security-Policy',
            present: response.headers.has('content-security-policy'),
            value: response.headers.get('content-security-policy') || undefined,
            severity: 'high',
            description: 'Prevents XSS and injection attacks',
          },
          {
            name: 'Strict-Transport-Security',
            present: response.headers.has('strict-transport-security'),
            value: response.headers.get('strict-transport-security') || undefined,
            severity: 'high',
            description: 'Enforces HTTPS connections',
          },
          {
            name: 'X-Frame-Options',
            present: response.headers.has('x-frame-options'),
            value: response.headers.get('x-frame-options') || undefined,
            severity: 'medium',
            description: 'Prevents clickjacking attacks',
          },
          {
            name: 'X-Content-Type-Options',
            present: response.headers.has('x-content-type-options'),
            value: response.headers.get('x-content-type-options') || undefined,
            severity: 'medium',
            description: 'Prevents MIME sniffing',
          },
          {
            name: 'Referrer-Policy',
            present: response.headers.has('referrer-policy'),
            value: response.headers.get('referrer-policy') || undefined,
            severity: 'medium',
            description: 'Controls referrer information',
          },
        ];

        setHeaders(checkedHeaders);
        
        // Show alert if any high or critical headers are missing
        const missingCriticalHeaders = checkedHeaders.some(
          (h) => !h.present && (h.severity === 'critical' || h.severity === 'high')
        );
        setShowAlert(missingCriticalHeaders);
      } catch (error) {
        console.error('Failed to check security headers:', error);
      }
    };

    checkHeaders();
  }, [canView, isDevelopment]);

  if (isDevelopment || !canView || !showAlert) return null;

  const missingHeaders = headers.filter((h) => !h.present);
  const presentHeaders = headers.filter((h) => h.present);

  return (
    <div className="fixed bottom-4 right-4 max-w-md z-50 space-y-2">
      {missingHeaders.length > 0 && (
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Security Headers Missing</AlertTitle>
          <AlertDescription className="space-y-2">
            <p className="text-sm">
              The following security headers are not configured:
            </p>
            <ul className="text-xs space-y-1 list-disc list-inside">
              {missingHeaders.map((header) => (
                <li key={header.name}>
                  <span className="font-semibold">{header.name}</span> -{' '}
                  {header.description}
                </li>
              ))}
            </ul>
            <p className="text-xs mt-2">
              Configure these in <code>public/_headers</code> for better security.
            </p>
          </AlertDescription>
        </Alert>
      )}
      
      {presentHeaders.length === headers.length && (
        <Alert className="bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800">
          <ShieldCheck className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertTitle className="text-green-800 dark:text-green-200">
            Security Headers Configured
          </AlertTitle>
          <AlertDescription className="text-green-700 dark:text-green-300 text-sm">
            All critical security headers are properly configured.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
